import Link from 'next/link';
import { format, fromUnixTime } from 'date-fns';
import { useTranslate } from '@tolgee/react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { NavigationItemFragment, TournamentDetailItemFragment } from '@amf/shared/types/graphql';
import { sdk } from '@amf/shared/lib/graphQlClient';
import { getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import Container from '@amf/shared/components/layout/Container';
import Button from '@amf/shared/components/button/Button';
import IconArrowLeft from '@amf/shared/icons/IconArrowLeft';

import Layout from 'components/layout/Layout';
import TournamentMap from 'components/tournament/TournamentMap';
import { AppLinks } from 'utils/links';
import Meta from 'components/Meta';

interface TournamentDetailPageProps extends NavigationProps {
  tournament: TournamentDetailItemFragment;
}

export default function TournamentDetailPage({
  tournament,
  navigation,
  partners,
}: TournamentDetailPageProps) {
  const { t } = useTranslate('malyfotbal');
  const dateStart = fromUnixTime(tournament.dateStart);
  const dateEnd = tournament.dateEnd ? fromUnixTime(tournament.dateEnd) : null;
  return (
    <div className='TournamentDetailPage'>
      <Layout navigation={navigation} partners={partners}>
        <Meta title={tournament.name} description={tournament.description} />
        <Container>
          <Link href={AppLinks.tournamentList}>
            <a>
              <Button variant='blue-outline' leftIcon={<IconArrowLeft />}>
                {t('tournaments.detail.back')}
              </Button>
            </a>
          </Link>
          <h1 className='TournamentDetailPage__title'>{tournament.name}</h1>
          <p className='TournamentDetailPage__subtitle'>
            {tournament.year && <span>{tournament.year}</span>}
            {dateEnd === null ? (
              <span>{format(dateStart, 'dd/MM/yyyy')}</span>
            ) : (
              <span>
                {format(dateStart, 'dd/MM')} - {format(dateEnd, 'dd/MM/yyyy')}
              </span>
            )}
            <span>{tournament.localityName}</span>
            <span>{t(`region.${tournament.region}`)}</span>
            <span>
              {t('tournaments.detail.category')}: {tournament.category.name}
            </span>
          </p>
          <div className='Content'>
            <p>{tournament.description}</p>
          </div>
          <div className='TournamentDetailPage__actions'>
            <p className='TournamentDetailPage__action TournamentDetailPage__action--text'>
              {t('tournaments.detail.fee')}:{' '}
              {tournament.registrationFee === 0 ? (
                <strong>{t('tournaments.detail.fee.free')}</strong>
              ) : (
                <strong>{tournament.registrationFee}&nbsp;Kč</strong>
              )}
            </p>
            <p className='TournamentDetailPage__action TournamentDetailPage__action--text'>
              {t('tournaments.detail.playType')}:{' '}
              <strong>{t(`tournaments.form.playType.option.${tournament.playType}`)}</strong>
            </p>
            <p className='TournamentDetailPage__action TournamentDetailPage__action--text'>
              {t('tournaments.detail.groundType')}:{' '}
              <strong>{t(`tournaments.form.groundType.option.${tournament.groundType}`)}</strong>
            </p>
            <p className='TournamentDetailPage__action TournamentDetailPage__action--text'>
              {t('tournaments.detail.email')}:{' '}
              <a href={`mailto:${tournament.authorEmail}`}>
                <strong>{tournament.authorEmail}</strong>
              </a>
            </p>
            {tournament.authorPhone && (
              <p className='TournamentDetailPage__action TournamentDetailPage__action--text'>
                {t('tournaments.detail.phone')}:{' '}
                <a href={`tel:${tournament.authorPhone.split(' ').join('')}`}>
                  <strong>{tournament.authorPhone}</strong>
                </a>
              </p>
            )}
            {tournament.link && (
              <div className='TournamentDetailPage__action'>
                <a href={tournament.link} target='_blank' rel='noreferrer'>
                  <Button variant='blue'>{t('tournaments.detail.link')}</Button>
                </a>
              </div>
            )}
          </div>
          <TournamentMap
            title={t('tournaments.detail.location')}
            description={tournament.localityName}
            lat={tournament.localityLatitude}
            lng={tournament.localityLongitude}
          />
        </Container>
      </Layout>
    </div>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const { tournamentList } = await sdk.TournamentList({
    union: process.env.NEXT_PUBLIC_UNION,
    limit: 1000,
    offset: 0,
    dateFrom: null,
    dateTo: null,
    category: null,
    region: null,
    query: null,
  });

  const paths: { params: { id: string } }[] = (tournamentList ?? []).map(t => ({
    params: { id: t.id },
  }));

  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const { id } = params as { id: string };

  let navigation: NavigationItemFragment[];
  let tournament: TournamentDetailItemFragment | null | undefined;

  try {
    const data = await sdk.TournamentDetailPage({
      id,
      union: process.env.NEXT_PUBLIC_UNION,
      locale: getLanguageLocale(locale!),
    });
    tournament = data.tournament;
    navigation = data.navigation;
  } catch {
    return {
      notFound: true,
    };
  }

  if (!tournament) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      navigation,
      tournament,
      partners: await getPartners(),
    },
    revalidate: 10,
  };
};
